import { Component } from '@theme/component';
import {
  addToRemovedUpsellId,
  clearLastAddedUpsellId,
  getCart,
  getLastAddedUpsellId,
  isPairedProductInCart,
  isRemovedUpsellId,
  setLastAddedUpsellId,
} from '@theme/cart-upsell-state';
import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

const FADE_IN_DURATION_MS = 300;
const VISIBILITY_DEBOUNCE_MS = 140;

/**
 * Cart Upsell Component
 * Manages visibility of the cart upsell based on cart state and user interactions.
 * - Shows upsell for the most recently added cart item that has a paired product (server-side).
 * - Hides upsell if the paired product was previously added and then removed (not shown again).
 */
class CartUpsellComponent extends Component {
  /** @type {boolean} */
  #miniPdpOpened = false;

  /** @type {string | null} */
  #pairedProductId = null;

  /** @type {boolean} - paired product was in cart on last check (used to detect "added then removed") */
  #pairedProductWasInCart = false;

  /** Debounced visibility check so cart:update + dialog:close don't each trigger a separate /cart.js */
  #runVisibilityCheckDebounced = debounce(() => {
    this.#runVisibilityCheck();
  }, VISIBILITY_DEBOUNCE_MS);

  connectedCallback() {
    super.connectedCallback();

    this.#pairedProductId = this.dataset.pairedProductId || null;

    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Track when trigger is clicked (mini PDP will open)
    const trigger = this.querySelector('.cart-upsell__trigger');
    if (trigger) {
      trigger.addEventListener('click', this.#handleCtaClick);
    }

    // Listen for mini PDP drawer close to check if product was added
    const miniPdpDrawer = document.querySelector('mini-pdp-drawer');
    if (miniPdpDrawer) {
      miniPdpDrawer.addEventListener('dialog:close', this.#handleDialogClose);
    }

    // Check initial state (fade-in runs via class from Liquid so it’s in sync with product shift)
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    const trigger = this.querySelector('.cart-upsell__trigger');
    if (trigger) {
      trigger.removeEventListener('click', this.#handleCtaClick);
    }

    const miniPdpDrawer = document.querySelector('mini-pdp-drawer');
    if (miniPdpDrawer) {
      miniPdpDrawer.removeEventListener('dialog:close', this.#handleDialogClose);
    }
  }

  /**
   * Handles cart update events. Uses debounced visibility check to avoid multiple /cart.js requests.
   * @param {CustomEvent} _event
   */
  #handleCartUpdate = (_event) => {
    this.#runVisibilityCheckDebounced();
  };

  /**
   * Single visibility check: re-read paired product from DOM, then show/hide based on cart.
   * Called by debounced runner from cart:update and dialog:close.
   */
  async #runVisibilityCheck() {
    // Re-read from DOM after morph so we use current paired product (Rule 7: show previous upsell when product removed)
    const previousId = this.#pairedProductId;
    this.#pairedProductId = this.dataset.pairedProductId || null;
    if (previousId !== this.#pairedProductId) {
      this.#pairedProductWasInCart = false;
    }

    if (this.#isPairedProductRemovedFromUpsell()) {
      this.#miniPdpOpened = false;
      this.classList.add('hidden');
      return;
    }

    const pairedProductInCart = await this.#checkIfPairedProductInCart();

    if (pairedProductInCart) {
      this.#pairedProductWasInCart = true;
      setLastAddedUpsellId(this.#pairedProductId);
      this.#miniPdpOpened = false;
      this.classList.add('hidden');
    } else {
      const wasAddedThenRemoved =
        this.#pairedProductWasInCart || getLastAddedUpsellId() === this.#pairedProductId?.toString();
      if (wasAddedThenRemoved) {
        addToRemovedUpsellId(this.#pairedProductId);
        clearLastAddedUpsellId();
        this.#pairedProductWasInCart = false;
        this.classList.add('hidden');
        return;
      }
      if (this.#miniPdpOpened) {
        const wasHidden = this.classList.contains('hidden');
        this.classList.remove('hidden');
        if (wasHidden) this.#triggerFadeIn();
      } else {
        this.#updateVisibility(pairedProductInCart);
      }
    }
  }

  /** Triggers fade-in animation when the upsell is shown (respects prefers-reduced-motion). */
  #triggerFadeIn() {
    this.classList.remove('cart-upsell--fade-in');
    // Re-add so animation runs (including when showing again after being hidden)
    requestAnimationFrame(() => {
      this.classList.add('cart-upsell--fade-in');
      setTimeout(() => this.classList.remove('cart-upsell--fade-in'), FADE_IN_DURATION_MS);
    });
  }

  /** Handles CTA button click (mini PDP will open). */
  #handleCtaClick = () => {
    this.#miniPdpOpened = true;
  };

  /** Handles dialog close event (mini PDP drawer closed). Uses same debounced visibility check. */
  #handleDialogClose = () => {
    setTimeout(() => this.#runVisibilityCheckDebounced(), 50);
  };

  /**
   * Checks if the paired product is currently in the cart. Uses shared getCart() and isPairedProductInCart().
   * @returns {Promise<boolean>}
   */
  async #checkIfPairedProductInCart() {
    if (!this.#pairedProductId) return false;
    try {
      const cart = await getCart();
      return isPairedProductInCart(cart, this.#pairedProductId);
    } catch (error) {
      console.error('Error checking cart:', error);
      return false;
    }
  }

  /**
   * Updates the visibility of the upsell based on current cart state.
   * @param {boolean} [knownPairedInCart] - When provided (e.g. from #runVisibilityCheck), skips a second getCart() call.
   */
  async #updateVisibility(knownPairedInCart) {
    this.#pairedProductId = this.dataset.pairedProductId || null;
    if (!this.#pairedProductId) {
      this.classList.add('hidden');
      return;
    }

    if (this.#isPairedProductRemovedFromUpsell()) {
      this.classList.add('hidden');
      return;
    }

    const pairedProductInCart =
      typeof knownPairedInCart === 'boolean'
        ? knownPairedInCart
        : await this.#checkIfPairedProductInCart();

    if (pairedProductInCart) {
      this.#pairedProductWasInCart = true;
      setLastAddedUpsellId(this.#pairedProductId);
      this.classList.add('hidden');
      this.#miniPdpOpened = false;
    } else if (getLastAddedUpsellId() === this.#pairedProductId?.toString()) {
      // Paired was just removed (e.g. after section morph); don't show again
      addToRemovedUpsellId(this.#pairedProductId);
      clearLastAddedUpsellId();
      this.classList.add('hidden');
    } else {
      const wasHidden = this.classList.contains('hidden');
      this.classList.remove('hidden');
      if (wasHidden) this.#triggerFadeIn();
    }
  }

  /** True if the current paired product was previously added as upsell and then removed. */
  #isPairedProductRemovedFromUpsell() {
    return isRemovedUpsellId(this.#pairedProductId);
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
